import logger from '@/utils/logger';
/**
 * PDF Export V3 - Version améliorée SANS ICONES
 * - Tableaux stylés professionnels
 * - Graphiques natifs qui marchent
 * - Style épuré et élégant
 * - Format A4 parfait
 */

import jsPDF from 'jspdf';

// ==========================================
// TYPES
// ==========================================

export interface ChartSpec {
 type: 'bar' | 'pie' | 'line' | 'scatter';
 title: string;
 data: Array<{
 label?: string;
 value?: number;
 // Champs alternatifs acceptés
 mois?: string;
 segment?: string;
 produit?: string;
 name?: string;
 phase?: string;
 ca?: number;
 ca_realise?: number;
 ca_potentiel?: number;
 montant?: number;
 total?: number;
 // Champs scatter
 x?: number;
 y?: number;
 risk_probability?: number;
 financial_impact?: number;
 budget?: number;
 size?: number;
 [key: string]: any; // Permet d'autres champs dynamiques
 }>;
 insight?: string;
 xAxis?: string; // Label axe X pour scatter
 yAxis?: string; // Label axe Y pour scatter
}

export interface PDFOptions {
 title: string;
 subtitle?: string;
 author?: string;
 timestamp?: boolean;
}

// ==========================================
// CONFIGURATION A4
// ==========================================

const CONFIG = {
 // Dimensions A4 en mm
 width: 210,
 height: 297,
 margin: 15,
 
 // Typographie
 fontSize: {
 title: 18,
 subtitle: 12,
 heading1: 14,
 heading2: 12,
 heading3: 11,
 body: 10,
 small: 8
 },
 
 // Espacement
 lineHeight: 6,
 paragraphSpacing: 4,
 
 // Couleurs professionnelles
 colors: {
 primary: [41, 128, 185] as [number, number, number], // Bleu professionnel
 secondary: [52, 73, 94] as [number, number, number], // Gris foncé
 text: [44, 62, 80] as [number, number, number], // Texte
 light: [220, 220, 220] as [number, number, number], // Bordures
 lighter: [245, 245, 245] as [number, number, number], // Fond
 success: [46, 204, 113] as [number, number, number], // Vert
 warning: [241, 196, 15] as [number, number, number], // Jaune
 danger: [231, 76, 60] as [number, number, number], // Rouge
 
 // Palette graphiques (8 couleurs harmonieuses)
 charts: [
 [41, 128, 185], // Bleu
 [46, 204, 113], // Vert
 [241, 196, 15], // Jaune
 [231, 76, 60], // Rouge
 [155, 89, 182], // Violet
 [26, 188, 156], // Turquoise
 [230, 126, 34], // Orange
 [149, 165, 166] // Gris
 ] as Array<[number, number, number]>
 }
};

// ==========================================
// CLASSE PRINCIPALE
// ==========================================

export class PDFExporter {
 private pdf: jsPDF;
 private y: number;
 private maxWidth: number;
 private pageHeight: number;
 private margin: number;
 
 constructor(options: PDFOptions) {
 this.pdf = new jsPDF('p', 'mm', 'a4');
 this.y = CONFIG.margin;
 this.maxWidth = CONFIG.width - (CONFIG.margin * 2);
 this.pageHeight = CONFIG.height - CONFIG.margin;
 this.margin = CONFIG.margin;
 
 // En-tête du document
 this.addHeader(options);
 }
 
 /**
 * Ajoute l'en-tête du PDF
 */
 private addHeader(options: PDFOptions): void {
 // Titre
 this.pdf.setFontSize(CONFIG.fontSize.title);
 this.pdf.setFont('helvetica', 'bold');
 this.pdf.setTextColor(...CONFIG.colors.primary);
 this.pdf.text(options.title, CONFIG.margin, this.y);
 this.y += 10;
 
 // Sous-titre
 if (options.subtitle) {
 this.pdf.setFontSize(CONFIG.fontSize.subtitle);
 this.pdf.setFont('helvetica', 'normal');
 this.pdf.setTextColor(...CONFIG.colors.secondary);
 this.pdf.text(options.subtitle, CONFIG.margin, this.y);
 this.y += 8;
 }
 
 // Timestamp
 if (options.timestamp) {
 this.pdf.setFontSize(CONFIG.fontSize.small);
 this.pdf.setTextColor(...CONFIG.colors.secondary);
 const date = new Date().toLocaleString('fr-FR', {
 year: 'numeric',
 month: 'long',
 day: 'numeric',
 hour: '2-digit',
 minute: '2-digit'
 });
 this.pdf.text(`Genere le ${date}`, CONFIG.margin, this.y);
 this.y += 6;
 }
 
 // Ligne séparatrice élégante
 this.pdf.setDrawColor(...CONFIG.colors.primary);
 this.pdf.setLineWidth(0.5);
 this.pdf.line(CONFIG.margin, this.y, CONFIG.width - CONFIG.margin, this.y);
 this.y += 10;
 }
 
 /**
 * Vérifie si on doit ajouter une nouvelle page
 */
 private checkPageBreak(requiredSpace: number = 20): void {
 if (this.y + requiredSpace > this.pageHeight) {
 this.pdf.addPage();
 this.y = CONFIG.margin;
 }
 }
 
 /**
 * Ajoute du texte avec retour à ligne automatique
 */
 addText(text: string, style: 'normal' | 'bold' = 'normal'): void {
 this.pdf.setFontSize(CONFIG.fontSize.body);
 this.pdf.setFont('helvetica', style);
 this.pdf.setTextColor(...CONFIG.colors.text);
 
 // Diviser le texte en lignes qui tiennent dans la largeur
 const lines = this.pdf.splitTextToSize(text, this.maxWidth);
 
 lines.forEach((line: string) => {
 this.checkPageBreak();
 this.pdf.text(line, CONFIG.margin, this.y);
 this.y += CONFIG.lineHeight;
 });
 
 this.y += CONFIG.paragraphSpacing;
 }
 
 /**
 * Ajoute un titre (heading)
 */
 addHeading(text: string, level: 1 | 2 | 3 = 1): void {
 this.checkPageBreak(15);
 
 const fontSize = level === 1 ? CONFIG.fontSize.heading1 : 
 level === 2 ? CONFIG.fontSize.heading2 : 
 CONFIG.fontSize.heading3;
 
 this.pdf.setFontSize(fontSize);
 this.pdf.setFont('helvetica', 'bold');
 this.pdf.setTextColor(...CONFIG.colors.primary);
 
 const lines = this.pdf.splitTextToSize(text, this.maxWidth);
 lines.forEach((line: string) => {
 this.pdf.text(line, CONFIG.margin, this.y);
 this.y += CONFIG.lineHeight + 2;
 });
 
 this.y += 4;
 }
 
 /**
 * Ajoute un paragraphe avec parsing markdown simple
 */
 addParagraph(text: string): void {
 // Ignorer les lignes vides de graphiques non générés
 if (!text || text.trim() === '') return;
 if (text.includes('SPECIFICATION JSON:') || text.includes('Graphique') && text.split(' ').length < 4) {
 // Ligne titre de graphique sans JSON - on skip
 return;
 }
 
 this.pdf.setFontSize(CONFIG.fontSize.body);
 this.pdf.setTextColor(...CONFIG.colors.text);
 
 // Diviser en lignes qui respectent la largeur STRICTEMENT
 const lines = this.pdf.splitTextToSize(text, this.maxWidth);
 
 lines.forEach((line: string) => {
 this.checkPageBreak();
 
 // Détecter et gérer le gras **texte**
 if (line.includes('**')) {
 this.renderLineWithBold(line);
 } else {
 this.pdf.setFont('helvetica', 'normal');
 this.pdf.text(line, CONFIG.margin, this.y);
 }
 
 this.y += CONFIG.lineHeight;
 });
 
 this.y += CONFIG.paragraphSpacing;
 }
 
 /**
 * Rend une ligne avec parties en gras
 */
 private renderLineWithBold(line: string): void {
 let x = CONFIG.margin;
 const parts = line.split('**');
 
 parts.forEach((part, index) => {
 if (!part) return;
 
 // Alterner entre normal et gras
 const isBold = index % 2 === 1;
 this.pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
 
 this.pdf.text(part, x, this.y);
 x += this.pdf.getTextWidth(part);
 });
 }
 
 /**
 * Ajoute un tableau stylé professionnel - AMELIORE
 */
 addTable(rows: string[][]): void {
 if (rows.length === 0) return;
 
 const colCount = rows[0].length;
 const cellPadding = 2;
 const minCellWidth = 20;
 
 // Calculer largeurs optimales
 const colWidths: number[] = [];
 for (let col = 0; col < colCount; col++) {
 let maxWidth = minCellWidth;
 rows.forEach(row => {
 if (row[col]) {
 const textWidth = this.pdf.getTextWidth(row[col]) + (cellPadding * 2);
 maxWidth = Math.max(maxWidth, textWidth);
 }
 });
 colWidths.push(Math.min(maxWidth, this.maxWidth / colCount));
 }
 
 // Ajuster pour tenir dans la page
 const totalWidth = colWidths.reduce((sum, w) => sum + w, 0);
 if (totalWidth > this.maxWidth) {
 const scale = this.maxWidth / totalWidth;
 colWidths.forEach((w, i) => colWidths[i] = w * scale);
 }
 
 this.checkPageBreak(rows.length * 10 + 10);
 
 rows.forEach((row, rowIndex) => {
 // Calculer hauteur nécessaire avec wrapping
 const rowHeight = this.calculateRowHeight(row, colWidths, cellPadding);
 
 this.checkPageBreak(rowHeight);
 
 let x = CONFIG.margin;
 const startY = this.y;
 
 row.forEach((cell, colIndex) => {
 const cellWidth = colWidths[colIndex];
 
 // En-tête avec style spécial
 if (rowIndex === 0) {
 // Fond bleu pour l'en-tête
 this.pdf.setFillColor(41, 128, 185);
 this.pdf.rect(x, startY - 5, cellWidth, rowHeight, 'F');
 
 // Texte blanc en gras
 this.pdf.setFont('helvetica', 'bold');
 this.pdf.setTextColor(255, 255, 255);
 } else {
 // Alternance de couleurs pour les lignes
 if (rowIndex % 2 === 0) {
 this.pdf.setFillColor(245, 245, 245);
 this.pdf.rect(x, startY - 5, cellWidth, rowHeight, 'F');
 }
 
 this.pdf.setFont('helvetica', 'normal');
 this.pdf.setTextColor(...CONFIG.colors.text);
 }
 
 // Bordure
 this.pdf.setDrawColor(...CONFIG.colors.light);
 this.pdf.setLineWidth(0.3);
 this.pdf.rect(x, startY - 5, cellWidth, rowHeight);
 
 // Texte avec wrapping AMELIORE
 this.pdf.setFontSize(CONFIG.fontSize.small - 1);
 const textWidth = cellWidth - (cellPadding * 2);
 const wrappedLines = this.pdf.splitTextToSize(cell, textWidth);
 
 let textY = startY;
 const maxLines = 6; // Plus de lignes autorisées
 
 wrappedLines.slice(0, maxLines).forEach((textLine: string) => {
 this.pdf.text(textLine, x + cellPadding, textY);
 textY += 4;
 });
 
 // Indicateur si texte tronqué
 if (wrappedLines.length > maxLines) {
 this.pdf.setFontSize(CONFIG.fontSize.small - 2);
 this.pdf.setTextColor(150, 150, 150);
 this.pdf.text('...', x + cellWidth - cellPadding - 3, textY - 1);
 }
 
 x += cellWidth;
 });
 
 this.y = startY + rowHeight;
 });
 
 this.y += CONFIG.paragraphSpacing + 2;
 }
 
 /**
 * Calcule la hauteur nécessaire pour une ligne de tableau - AMELIORE
 */
 private calculateRowHeight(row: string[], colWidths: number[], padding: number): number {
 let maxLines = 1;
 
 row.forEach((cell, i) => {
 const textWidth = colWidths[i] - (padding * 2);
 const lines = this.pdf.splitTextToSize(cell, textWidth);
 maxLines = Math.max(maxLines, Math.min(lines.length, 6)); // Augmenté à 6 lignes
 });
 
 return Math.max(10, maxLines * 4 + 4); // Plus d'espace
 }
 
 /**
 * Ajoute un graphique en barres AMELIORE
 */
 addBarChart(data: ChartSpec): void {
 if (!data || !data.data || !Array.isArray(data.data) || data.data.length === 0) {
   logger.warn(' Graphique bar sans données valides');
   return;
 }
 
 // DEBUG: Logger les données brutes
 logger.log('[PDF] Bar chart raw data:', {
   title: data.title,
   dataLength: data.data.length,
   firstItem: data.data[0],
   allKeys: data.data[0] ? Object.keys(data.data[0]) : []
 });
 
 // Normaliser les données : chercher value, ca, montant, etc. (ROBUSTE - IDENTIQUE à FluidChat)
 const normalizedData = data.data.map(item => {
   if (!item || typeof item !== 'object') return null;
   
   // Extraire le label (premier champ texte trouvé) - IDENTIQUE à FluidChat
   const labelKey = Object.keys(item).find(k => 
     typeof item[k] === 'string' || 
     k.toLowerCase().includes('label') || 
     k.toLowerCase().includes('name') || 
     k.toLowerCase().includes('mois') || 
     k.toLowerCase().includes('produit') || 
     k.toLowerCase().includes('segment')
   );
   const label = labelKey ? item[labelKey] : 'Sans nom';
   
   // Extraire la valeur (premier champ numérique trouvé) - IDENTIQUE à FluidChat + SUPPORT COMPLET
   const valueKey = Object.keys(item).find(k => 
     typeof item[k] === 'number' || 
     k.toLowerCase().includes('value') || 
     k.toLowerCase().includes('ca') || 
     k.toLowerCase().includes('amount') || 
     k.toLowerCase().includes('potentiel') || 
     k.toLowerCase().includes('montant') ||
     k.toLowerCase().includes('total') ||
     k.toLowerCase().includes('budget') ||
     k.toLowerCase().includes('cumul') ||
     k.toLowerCase().includes('impact') ||
     k.toLowerCase().includes('probabilite') ||
     k.toLowerCase().includes('probability')
   );
   // Extraire la valeur - support de tous les types numériques
   let value = 0;
   if (valueKey) {
     const rawValue = item[valueKey];
     if (typeof rawValue === 'number') {
       value = rawValue;
     } else if (typeof rawValue === 'string') {
       // Parser les nombres avec virgules, espaces, etc.
       value = parseFloat(rawValue.replace(/[,\s]/g, '')) || 0;
     } else {
       value = 0;
     }
   } else {
     // Si pas de clé valeur trouvée, chercher le premier champ numérique
     for (const key of Object.keys(item)) {
       if (typeof item[key] === 'number' && key !== 'pourcentage' && key !== 'percentage') {
         value = item[key];
         break;
       }
     }
   }
   
   return { label, value };
 }).filter(d => d && d !== null);
 
 // Filtrer les données invalides (accepter les valeurs à 0 pour certains graphiques)
 const validData = normalizedData.filter(item => 
 item && 
 typeof item.value === 'number' && 
 isFinite(item.value) &&
 !isNaN(item.value) &&
 item.label && 
 item.label.trim().length > 0
 );
 
 if (validData.length === 0) {
   logger.warn('[PDF] Graphique bar ignoré (aucune donnée valide)', {
     normalizedData,
     validData
   });
   return;
 }
 
 logger.log('[PDF] Bar chart validé:', {
   title: data.title,
   validCount: validData.length,
   sample: validData.slice(0, 3)
 });
 
 this.checkPageBreak(100);
 
 const chartWidth = this.maxWidth - 20;
 const chartHeight = 70;
 const startX = CONFIG.margin + 10;
 const barSpacing = 8;
 const totalBars = validData.length;
 const availableWidth = chartWidth - (barSpacing * (totalBars - 1));
 const barWidth = Math.min(availableWidth / totalBars, 25);
 
 const maxValue = Math.max(...validData.map(d => d.value));
 const scale = (chartHeight - 15) / (maxValue || 1);
 
 // Titre du graphique
 this.pdf.setFontSize(CONFIG.fontSize.subtitle);
 this.pdf.setFont('helvetica', 'bold');
 this.pdf.setTextColor(...CONFIG.colors.primary);
 this.pdf.text(data.title, CONFIG.margin, this.y);
 this.y += 10;
 
 const baseY = this.y + chartHeight;
 
 // Grille de fond
 this.pdf.setDrawColor(240, 240, 240);
 this.pdf.setLineWidth(0.2);
 for (let i = 0; i <= 5; i++) {
 const gridY = this.y + (chartHeight * i / 5);
 this.pdf.line(startX, gridY, startX + chartWidth, gridY);
 
 // Valeur sur l'axe Y
 const value = Math.round((maxValue * (5 - i) / 5));
 this.pdf.setFontSize(7);
 this.pdf.setTextColor(150, 150, 150);
 this.pdf.text(value.toString(), startX - 5, gridY + 1, { align: 'right' });
 }
 
 // Axe X principal
 this.pdf.setDrawColor(...CONFIG.colors.secondary);
 this.pdf.setLineWidth(0.5);
 this.pdf.line(startX, baseY, startX + chartWidth, baseY);
 
 // Barres avec effet 3D léger
 validData.forEach((item, index) => {
 
 const barHeight = item.value * scale;
 const x = startX + (index * (barWidth + barSpacing));
 const y = baseY - barHeight;
 
 // Couleur de la barre
 const color = CONFIG.colors.charts[index % CONFIG.colors.charts.length];
 this.pdf.setFillColor(...color);
 
 // Barre principale
 this.pdf.rect(x, y, barWidth, barHeight, 'F');
 
 // Bordure pour définition
 this.pdf.setDrawColor(color[0] - 20, color[1] - 20, color[2] - 20);
 this.pdf.setLineWidth(0.5);
 this.pdf.rect(x, y, barWidth, barHeight);
 
 // Valeur au-dessus de la barre
 this.pdf.setFontSize(CONFIG.fontSize.small);
 this.pdf.setFont('helvetica', 'bold');
 this.pdf.setTextColor(...CONFIG.colors.text);
 const valueText = item.value.toLocaleString('fr-FR');
 this.pdf.text(valueText, x + barWidth / 2, y - 2, { align: 'center' });
 
 // Label en dessous
 this.pdf.setFontSize(CONFIG.fontSize.small - 1);
 this.pdf.setFont('helvetica', 'normal');
 this.pdf.setTextColor(...CONFIG.colors.secondary);
 const label = (item.label || `Item ${index + 1}`).toString();
 const displayLabel = label.length > 10 ? label.substring(0, 8) + '..' : label;
 this.pdf.text(displayLabel, x + barWidth / 2, baseY + 6, { align: 'center' });
 });
 
 this.y = baseY + 15;
 }
 
 /**
 * Ajoute un graphique camembert AMELIORE
 */
 addPieChart(data: ChartSpec): void {
 if (!data || !data.data || !Array.isArray(data.data) || data.data.length === 0) {
   logger.warn(' Graphique pie sans données valides');
   return;
 }
 
 // DEBUG: Logger les données brutes
 logger.log('[PDF] Pie chart raw data:', {
   title: data.title,
   dataLength: data.data.length,
   firstItem: data.data[0],
   allKeys: data.data[0] ? Object.keys(data.data[0]) : []
 });
 
 // Normaliser les données : utiliser la même logique robuste que pour les bar charts
 const normalizedData = data.data.map(item => {
   if (!item || typeof item !== 'object') return null;
   
   // Extraire le label (premier champ texte trouvé) - SUPPORT COMPLET
   const labelKey = Object.keys(item).find(k => 
     typeof item[k] === 'string' || 
     k.toLowerCase().includes('label') || 
     k.toLowerCase().includes('name') || 
     k.toLowerCase().includes('mois') || 
     k.toLowerCase().includes('produit') || 
     k.toLowerCase().includes('segment') ||
     k.toLowerCase().includes('phase') ||
     k.toLowerCase().includes('lot') ||
     k.toLowerCase().includes('categorie') ||
     k.toLowerCase().includes('item') ||
     k.toLowerCase().includes('type') ||
     k.toLowerCase().includes('poste')
   );
   const label = labelKey ? item[labelKey] : 'Sans nom';
   
   // Extraire la valeur (premier champ numérique trouvé) - SUPPORT COMPLET
   const valueKey = Object.keys(item).find(k => 
     typeof item[k] === 'number' || 
     k.toLowerCase().includes('value') || 
     k.toLowerCase().includes('ca') || 
     k.toLowerCase().includes('amount') || 
     k.toLowerCase().includes('potentiel') || 
     k.toLowerCase().includes('montant') ||
     k.toLowerCase().includes('total') ||
     k.toLowerCase().includes('budget') ||
     k.toLowerCase().includes('cumul') ||
     k.toLowerCase().includes('impact') ||
     k.toLowerCase().includes('probabilite') ||
     k.toLowerCase().includes('probability')
   );
   
   // Extraire la valeur - support de tous les types numériques
   let value = 0;
   if (valueKey) {
     const rawValue = item[valueKey];
     if (typeof rawValue === 'number') {
       value = rawValue;
     } else if (typeof rawValue === 'string') {
       // Parser les nombres avec virgules, espaces, etc.
       value = parseFloat(rawValue.replace(/[,\s]/g, '')) || 0;
     } else {
       value = 0;
     }
   } else {
     // Si pas de clé valeur trouvée, chercher le premier champ numérique
     for (const key of Object.keys(item)) {
       if (typeof item[key] === 'number' && key !== 'pourcentage' && key !== 'percentage') {
         value = item[key];
         break;
       }
     }
   }
   
   return { label, value };
 }).filter(d => d && d !== null);

 // Filtrer les données invalides (accepter les valeurs à 0 pour certains graphiques)
 const validData = normalizedData.filter(item => 
 item && 
 typeof item.value === 'number' && 
 isFinite(item.value) &&
 !isNaN(item.value) &&
 item.value > 0 && // Pour pie chart, on veut exclure les valeurs à 0
 item.label && 
 item.label.trim().length > 0
 );
 
 if (validData.length === 0) {
 logger.log(' Graphique pie ignoré (aucune donnée valide)');
 return;
 }
 
 logger.log(' Pie chart:', data.title, '- Données valides:', validData.length);
 
 this.checkPageBreak(100);
 
 const centerX = CONFIG.margin + 45;
 const centerY = this.y + 45;
 const radius = 35;
 
 // Titre du graphique
 this.pdf.setFontSize(CONFIG.fontSize.subtitle);
 this.pdf.setFont('helvetica', 'bold');
 this.pdf.setTextColor(...CONFIG.colors.primary);
 this.pdf.text(data.title, CONFIG.margin, this.y);
 this.y += 10;
 
 const total = validData.reduce((sum, item) => sum + item.value, 0);
 if (total === 0) return;
 
 let currentAngle = -90; // Commencer en haut
 
 // Dessiner les parts du camembert avec PRÉCISION
 validData.forEach((item, index) => {
 const sliceAngle = (item.value / total) * 360;
 const color = CONFIG.colors.charts[index % CONFIG.colors.charts.length];
 
 this.pdf.setFillColor(...color);
 
 // Angles en radians
 const startRad = (currentAngle * Math.PI) / 180;
 const endRad = ((currentAngle + sliceAngle) * Math.PI) / 180;
 
 // Nombre de segments : 1 par degré (max 60 pour grands angles)
 const segments = Math.min(Math.max(Math.ceil(sliceAngle), 3), 60);
 const angleStep = (endRad - startRad) / segments;
 
 // Dessiner le secteur avec des triangles précis
 for (let i = 0; i < segments; i++) {
 const a1 = startRad + (i * angleStep);
 const a2 = startRad + ((i + 1) * angleStep);
 
 const x1 = centerX + radius * Math.cos(a1);
 const y1 = centerY + radius * Math.sin(a1);
 const x2 = centerX + radius * Math.cos(a2);
 const y2 = centerY + radius * Math.sin(a2);
 
 this.pdf.triangle(centerX, centerY, x1, y1, x2, y2, 'F');
 }
 
 // Bordure fine entre les parts
 this.pdf.setDrawColor(255, 255, 255);
 this.pdf.setLineWidth(0.5);
 this.pdf.line(centerX, centerY, centerX + radius * Math.cos(startRad), centerY + radius * Math.sin(startRad));
 
 currentAngle += sliceAngle;
 });
 
 // Bordure extérieure du cercle pour finition propre
 this.pdf.setDrawColor(200, 200, 200);
 this.pdf.setLineWidth(0.3);
 this.pdf.circle(centerX, centerY, radius, 'D');
 
 // Cercle blanc au centre pour effet donut (optionnel)
 // this.pdf.setFillColor(255, 255, 255);
 // this.pdf.circle(centerX, centerY, radius / 3, 'F');
 
 // Légende élégante à droite
 let legendY = this.y + 10;
 const legendX = CONFIG.margin + 95;
 
 validData.forEach((item, index) => {
 
 const color = CONFIG.colors.charts[index % CONFIG.colors.charts.length];
 
 // Carré de couleur avec bordure
 this.pdf.setFillColor(...color);
 this.pdf.rect(legendX, legendY - 3, 6, 6, 'F');
 this.pdf.setDrawColor(...CONFIG.colors.secondary);
 this.pdf.setLineWidth(0.3);
 this.pdf.rect(legendX, legendY - 3, 6, 6);
 
 // Texte de la légende
 this.pdf.setFontSize(CONFIG.fontSize.small);
 this.pdf.setFont('helvetica', 'normal');
 this.pdf.setTextColor(...CONFIG.colors.text);
 const percent = ((item.value / total) * 100).toFixed(1);
 const label = (item.label || `Segment ${index + 1}`).toString();
 const legendText = `${label}: ${percent}%`;
 this.pdf.text(legendText, legendX + 9, legendY + 1);
 
 legendY += 8;
 });
 
 this.y = Math.max(centerY + radius + 10, legendY + 5);
 }
 
 /**
 * Ajoute un graphique linéaire AMELIORE
 */
 addLineChart(data: ChartSpec): void {
 if (!data.data || data.data.length === 0) return;
 
 this.checkPageBreak(100);
 
 const chartWidth = this.maxWidth - 20;
 const chartHeight = 70;
 const startX = CONFIG.margin + 10;
 
 // Normaliser les données : utiliser la même logique robuste que pour les autres graphiques
 const normalizedData = data.data.map(item => {
   if (!item || typeof item !== 'object') return null;
   
   // Extraire le label (premier champ texte trouvé) - SUPPORT COMPLET
   const labelKey = Object.keys(item).find(k => 
     typeof item[k] === 'string' || 
     k.toLowerCase().includes('label') || 
     k.toLowerCase().includes('name') || 
     k.toLowerCase().includes('mois') || 
     k.toLowerCase().includes('produit') || 
     k.toLowerCase().includes('segment') ||
     k.toLowerCase().includes('phase') ||
     k.toLowerCase().includes('lot') ||
     k.toLowerCase().includes('categorie') ||
     k.toLowerCase().includes('item') ||
     k.toLowerCase().includes('type') ||
     k.toLowerCase().includes('poste')
   );
   const label = labelKey ? item[labelKey] : 'Sans nom';
   
   // Extraire la valeur (premier champ numérique trouvé) - SUPPORT COMPLET
   const valueKey = Object.keys(item).find(k => 
     typeof item[k] === 'number' || 
     k.toLowerCase().includes('value') || 
     k.toLowerCase().includes('ca') || 
     k.toLowerCase().includes('amount') || 
     k.toLowerCase().includes('potentiel') || 
     k.toLowerCase().includes('montant') ||
     k.toLowerCase().includes('total') ||
     k.toLowerCase().includes('budget') ||
     k.toLowerCase().includes('cumul') ||
     k.toLowerCase().includes('impact') ||
     k.toLowerCase().includes('probabilite') ||
     k.toLowerCase().includes('probability') ||
     k.toLowerCase().includes('burn_rate') ||
     k.toLowerCase().includes('burn') ||
     k.toLowerCase().includes('rate') ||
     k.toLowerCase().includes('duree') ||
     k.toLowerCase().includes('coût') ||
     k.toLowerCase().includes('cout') ||
     k.toLowerCase().includes('cost')
   );
   
   // Extraire la valeur - support de tous les types numériques
   let value = 0;
   if (valueKey) {
     const rawValue = item[valueKey];
     if (typeof rawValue === 'number') {
       value = rawValue;
     } else if (typeof rawValue === 'string') {
       // Parser les nombres avec virgules, espaces, etc.
       value = parseFloat(rawValue.replace(/[,\s]/g, '')) || 0;
     } else {
       value = 0;
     }
   } else {
     // Si pas de clé valeur trouvée, chercher le premier champ numérique
     for (const key of Object.keys(item)) {
       if (typeof item[key] === 'number' && key !== 'pourcentage' && key !== 'percentage') {
         value = item[key];
         break;
       }
     }
   }
   
   return { label, value };
 }).filter(d => d && d !== null);

 const values = normalizedData.map(d => d.value).filter(v => typeof v === 'number' && isFinite(v) && !isNaN(v));
 if (values.length === 0) {
 logger.warn(' Line chart: aucune donnée valide');
 return;
 }
 
 logger.log(' Line chart:', data.title, '- Données normalisées:', normalizedData.length);
 
 const maxValue = Math.max(...values);
 const minValue = Math.min(...values);
 const range = maxValue - minValue || 1;
 
 // Titre du graphique
 this.pdf.setFontSize(CONFIG.fontSize.subtitle);
 this.pdf.setFont('helvetica', 'bold');
 this.pdf.setTextColor(...CONFIG.colors.primary);
 this.pdf.text(data.title, CONFIG.margin, this.y);
 this.y += 10;
 
 const baseY = this.y + chartHeight;
 
 // Grille de fond
 this.pdf.setDrawColor(240, 240, 240);
 this.pdf.setLineWidth(0.2);
 for (let i = 0; i <= 5; i++) {
 const gridY = this.y + (chartHeight * i / 5);
 this.pdf.line(startX, gridY, startX + chartWidth, gridY);
 
 // Valeurs sur l'axe Y
 const value = minValue + (range * (5 - i) / 5);
 this.pdf.setFontSize(7);
 this.pdf.setTextColor(150, 150, 150);
 this.pdf.text(value.toFixed(0), startX - 5, gridY + 1, { align: 'right' });
 }
 
 // Axe X
 this.pdf.setDrawColor(...CONFIG.colors.secondary);
 this.pdf.setLineWidth(0.5);
 this.pdf.line(startX, baseY, startX + chartWidth, baseY);
 
 // Points et lignes
 const pointSpacing = chartWidth / (normalizedData.length - 1 || 1);
 const color = CONFIG.colors.charts[0];
 
 // Zone sous la courbe (remplissage semi-transparent simulé)
 this.pdf.setFillColor(color[0], color[1], color[2], 0.1);
 
 // Ligne principale
 this.pdf.setDrawColor(...color);
 this.pdf.setLineWidth(2);
 
 for (let i = 0; i < normalizedData.length - 1; i++) {
 const item = normalizedData[i];
 const nextItem = normalizedData[i + 1];
 
 if (typeof item.value !== 'number' || typeof nextItem.value !== 'number') continue;
 if (!isFinite(item.value) || !isFinite(nextItem.value)) continue;
 
 const x1 = startX + (i * pointSpacing);
 const normalizedY1 = (item.value - minValue) / range;
 const y1 = baseY - (normalizedY1 * chartHeight);
 
 const x2 = startX + ((i + 1) * pointSpacing);
 const normalizedY2 = (nextItem.value - minValue) / range;
 const y2 = baseY - (normalizedY2 * chartHeight);
 
 if (isFinite(x1) && isFinite(y1) && isFinite(x2) && isFinite(y2)) {
 this.pdf.line(x1, y1, x2, y2);
 }
 }
 
 // Points sur la ligne
 normalizedData.forEach((item, index) => {
 if (typeof item.value !== 'number' || !isFinite(item.value)) return;
 
 const x = startX + (index * pointSpacing);
 const normalizedY = (item.value - minValue) / range;
 const y = baseY - (normalizedY * chartHeight);
 
 if (!isFinite(x) || !isFinite(y)) return;
 
 // Point blanc avec bordure colorée
 this.pdf.setFillColor(255, 255, 255);
 this.pdf.circle(x, y, 2, 'F');
 this.pdf.setDrawColor(...color);
 this.pdf.setLineWidth(1.5);
 this.pdf.circle(x, y, 2, 'D');
 });
 
 // Labels sous l'axe
 this.pdf.setFontSize(CONFIG.fontSize.small - 1);
 this.pdf.setFont('helvetica', 'normal');
 this.pdf.setTextColor(...CONFIG.colors.secondary);
 
 normalizedData.forEach((item, index) => {
 if (index % Math.max(1, Math.ceil(normalizedData.length / 8)) === 0) {
 const x = startX + (index * pointSpacing);
 const label = item.label.length > 8 ? item.label.substring(0, 6) + '..' : item.label;
 this.pdf.text(label, x, baseY + 6, { align: 'center' });
 }
 });
 
 this.y = baseY + 15;
 }
 
 /**
 * Ajoute un graphique scatter (nuage de points / matrice)
 */
 addScatterChart(data: ChartSpec): void {
 if (!data || !data.data || !Array.isArray(data.data) || data.data.length === 0) {
 logger.warn(' Graphique scatter sans données valides');
 return;
 }
 
 // Normaliser les données scatter : chercher x, y, labels
 const normalizedData = data.data.map(item => {
 // Détecter les propriétés x et y (risk_probability, financial_impact, etc.)
 const xValue = item.risk_probability || item.x || item.risque || 0;
 const yValue = item.financial_impact || item.y || item.impact || item.budget || 0;
 const sizeValue = item.budget || item.size || item.ca || 1000;
 const label = item.phase || item.label || item.name || item.produit || '';
 
 return {
 x: typeof xValue === 'number' ? xValue : 0,
 y: typeof yValue === 'number' ? yValue : 0,
 size: typeof sizeValue === 'number' ? sizeValue : 1000,
 label: String(label)
 };
 });
 
 // Filtrer les données invalides
 const validData = normalizedData.filter(item => 
 typeof item.x === 'number' && isFinite(item.x) &&
 typeof item.y === 'number' && isFinite(item.y) &&
 item.label
 );
 
 if (validData.length === 0) {
 logger.log(' Graphique scatter ignoré (aucune donnée valide)');
 return;
 }
 
 logger.log(' Scatter chart:', data.title, '- Points valides:', validData.length);
 
 this.checkPageBreak(110);
 
 const chartWidth = this.maxWidth - 30;
 const chartHeight = 70;
 const startX = CONFIG.margin + 20;
 
 // Trouver min/max pour les axes
 const xValues = validData.map(d => d.x);
 const yValues = validData.map(d => d.y);
 const minX = Math.min(...xValues, 0);
 const maxX = Math.max(...xValues);
 const minY = Math.min(...yValues, 0);
 const maxY = Math.max(...yValues);
 
 const xRange = maxX - minX || 1;
 const yRange = maxY - minY || 1;
 
 // Titre du graphique
 this.pdf.setFontSize(CONFIG.fontSize.subtitle);
 this.pdf.setFont('helvetica', 'bold');
 this.pdf.setTextColor(...CONFIG.colors.primary);
 this.pdf.text(data.title, CONFIG.margin, this.y);
 this.y += 10;
 
 const baseY = this.y + chartHeight;
 
 // Grille de fond
 this.pdf.setDrawColor(240, 240, 240);
 this.pdf.setLineWidth(0.2);
 for (let i = 0; i <= 5; i++) {
 const gridY = this.y + (chartHeight * i / 5);
 this.pdf.line(startX, gridY, startX + chartWidth, gridY);
 
 // Valeur sur l'axe Y
 const value = minY + (yRange * (5 - i) / 5);
 this.pdf.setFontSize(7);
 this.pdf.setTextColor(150, 150, 150);
 this.pdf.text(value.toFixed(0), startX - 5, gridY + 1, { align: 'right' });
 }
 
 // Grille verticale
 for (let i = 0; i <= 5; i++) {
 const gridX = startX + (chartWidth * i / 5);
 this.pdf.line(gridX, this.y, gridX, baseY);
 
 // Valeur sur l'axe X
 const value = minX + (xRange * i / 5);
 this.pdf.setFontSize(7);
 this.pdf.setTextColor(150, 150, 150);
 this.pdf.text(value.toFixed(0), gridX, baseY + 5, { align: 'center' });
 }
 
 // Axes principaux
 this.pdf.setDrawColor(...CONFIG.colors.secondary);
 this.pdf.setLineWidth(0.8);
 this.pdf.line(startX, baseY, startX + chartWidth, baseY); // Axe X
 this.pdf.line(startX, this.y, startX, baseY); // Axe Y
 
 // Points scatter avec taille proportionnelle
 const maxSize = Math.max(...validData.map(d => d.size));
 const minPointRadius = 2;
 const maxPointRadius = 6;
 
 validData.forEach((item, index) => {
 // Position
 const xPos = startX + ((item.x - minX) / xRange) * chartWidth;
 const yPos = baseY - ((item.y - minY) / yRange) * chartHeight;
 
 if (!isFinite(xPos) || !isFinite(yPos)) return;
 
 // Taille du point proportionnelle
 const pointRadius = minPointRadius + ((item.size / maxSize) * (maxPointRadius - minPointRadius));
 
 // Couleur selon l'index
 const color = CONFIG.colors.charts[index % CONFIG.colors.charts.length];
 
 // Point principal
 this.pdf.setFillColor(...color);
 this.pdf.circle(xPos, yPos, pointRadius, 'F');
 
 // Bordure blanche pour visibilité
 this.pdf.setDrawColor(255, 255, 255);
 this.pdf.setLineWidth(0.5);
 this.pdf.circle(xPos, yPos, pointRadius, 'D');
 
 // Label si espace suffisant
 if (validData.length <= 10) {
 this.pdf.setFontSize(7);
 this.pdf.setTextColor(...CONFIG.colors.text);
 const labelText = item.label.length > 15 ? item.label.substring(0, 12) + '...' : item.label;
 this.pdf.text(labelText, xPos + pointRadius + 2, yPos + 1);
 }
 });
 
 // Labels des axes
 this.pdf.setFontSize(CONFIG.fontSize.small);
 this.pdf.setFont('helvetica', 'italic');
 this.pdf.setTextColor(...CONFIG.colors.secondary);
 
 // Label axe X (centré en bas)
 const xAxisLabel = data.xAxis || 'Risque (%)';
 this.pdf.text(xAxisLabel, startX + chartWidth / 2, baseY + 12, { align: 'center' });
 
 // Label axe Y (vertical à gauche)
 const yAxisLabel = data.yAxis || 'Impact (€)';
 this.pdf.text(yAxisLabel, CONFIG.margin + 2, this.y + chartHeight / 2, { angle: 90 });
 
 // Insight si présent
 if (data.insight) {
 this.y = baseY + 18;
 this.pdf.setFontSize(CONFIG.fontSize.small);
 this.pdf.setFont('helvetica', 'italic');
 this.pdf.setTextColor(...CONFIG.colors.primary);
 const insightLines = this.pdf.splitTextToSize(` ${data.insight}`, this.maxWidth);
 this.pdf.text(insightLines, CONFIG.margin, this.y);
 this.y += insightLines.length * 5;
 } else {
 this.y = baseY + 18;
 }
 }
 
 /**
  * Normaliser le type de chart pour l'export PDF
  */
 private normalizeChartType(type: string): 'bar' | 'pie' | 'line' | 'scatter' {
   if (!type) return 'bar';
   
   const normalized = type.toLowerCase().trim();
   
   // Normaliser tous les types vers les 4 types supportés - COMPLET
   // Types bar (tous les graphiques en barres)
   if (['stacked_bar', 'horizontal_bar', 'chart', 'waterfall', 'radar', 'bubble', 'gantt'].includes(normalized)) {
     return 'bar';
   }
   // Types pie (tous les graphiques circulaires)
   if (['doughnut', 'polararea', 'polar_area'].includes(normalized)) {
     return 'pie';
   }
   // Types line (tous les graphiques linéaires)
   if (['area'].includes(normalized)) {
     return 'line';
   }
   // Types déjà supportés directement
   if (['bar', 'pie', 'line', 'scatter'].includes(normalized)) {
     return normalized as 'bar' | 'pie' | 'line' | 'scatter';
   }
   // Fallback vers bar (le plus commun)
   logger.warn(`[PDF] Type de graphique inconnu "${type}", conversion en bar`);
   return 'bar';
 }

 /**
  * Réparer JSON mal formé de graphiques (ULTRA ROBUSTE)
  */
 private repairChartJSON(jsonStr: string): string {
 try {
 // Nettoyer le JSON de base
 let cleaned = jsonStr
 .replace(/\+(\d+)/g, '$1') // Retirer les + devant les nombres
 .replace(/,\s*}/g, '}') // Retirer virgules trainantes
 .replace(/,\s*]/g, ']'); // Retirer virgules trainantes dans arrays
 
 // Essayer de parser directement
 try {
 const parsed = JSON.parse(cleaned);
 if (parsed.type && parsed.data) return cleaned; // Déjà valide
 } catch (e) {
 // Pas encore valide, continuer réparation
 }
 
 // Détecter le type de graphique - SUPPORT TOUS TYPES (y compris waterfall, chart, etc.)
 const supportedChartTypes = ['bar', 'pie', 'line', 'chart', 'horizontal_bar', 'waterfall', 'scatter', 'area', 'doughnut', 'radar', 'bubble', 'polarArea', 'stacked_bar', 'gantt'];
 const typeMatch = cleaned.match(new RegExp(`"type"\\s*:\\s*"(${supportedChartTypes.join('|')})"`, 'i'));
 if (!typeMatch) return jsonStr;
 
 const chartType = typeMatch[1];
 
 // Extraire le title si présent
 const titleMatch = cleaned.match(/"title"\s*:\s*"([^"]+)"/);
 const title = titleMatch ? titleMatch[1] : 'Graphique';
 
 // Si "data" est déjà présent, juste nettoyer
 if (cleaned.includes('"data"')) {
 return cleaned;
 }
 
 // RECONSTRUCTION COMPLÈTE: extraire tous les objets { ... }
 const dataObjects: any[] = [];
 let depth = 0;
 let currentObj = '';
 let inString = false;
 let startedData = false;
 
 for (let i = 0; i < cleaned.length; i++) {
 const char = cleaned[i];
 const prevChar = i > 0 ? cleaned[i-1] : '';
 
 // Gérer les strings
 if (char === '"' && prevChar !== '\\') {
 inString = !inString;
 }
 
 if (inString) {
 if (startedData) currentObj += char;
 continue;
 }
 
 // Détecter début d'objet APRÈS le type
 if (char === '{') {
 depth++;
 if (depth === 2) { // Premier objet dans data
 startedData = true;
 currentObj = '{';
 } else if (startedData) {
 currentObj += char;
 }
 } else if (char === '}') {
 if (startedData) currentObj += char;
 depth--;
 
 // Objet data terminé
 if (depth === 1 && startedData && currentObj.length > 2) {
 try {
 const obj = JSON.parse(currentObj.replace(/\+(\d+)/g, '$1'));
 dataObjects.push(obj);
 currentObj = '';
 startedData = false;
 } catch (e) {
 // Objet invalide, continuer
 }
 }
 } else if (startedData) {
 currentObj += char;
 }
 }
 
 // Si on a extrait des objets, reconstruire le JSON complet
 if (dataObjects.length > 0) {
 const reconstructed = {
 type: chartType,
 title: title,
 data: dataObjects
 };
 return JSON.stringify(reconstructed);
 }
 
 // Sinon retourner l'original nettoyé
 return cleaned;
 
 } catch (e) {
 logger.warn(' [PDF] Réparation JSON échouée');
 return jsonStr;
 }
 }

 /**
 * Fonction unifiée pour détecter et parser un graphique JSON
 * Gère tous les formats: SPECIFICATION JSON, Graphique X -, JSON brut, ```json, titres markdown
 */
 private tryParseChart(lines: string[], startIndex: number): { chart: any | null; endIndex: number } {
 const maxSearch = 50; // Limite de recherche augmentée pour trouver tous les graphiques (était 35)
 let jsonStart = -1;
 let jsonEnd = -1;
 let jsonStr = '';
 
 const line = lines[startIndex].trim();
 
 // PATTERN 1: Titre markdown "### Graphique X -" suivi de "SPECIFICATION JSON:"
 // AMÉLIORÉ: Recherche plus large pour trouver tous les graphiques
 if (line.startsWith('###') && /Graphique\s+\d+\s*-/.test(line)) {
 // Chercher "SPECIFICATION JSON:" dans les lignes suivantes (ignorer lignes vides)
 // Augmenter maxSearch pour trouver les graphiques même s'ils sont plus loin
 const extendedSearch = Math.max(maxSearch, 50);
 for (let j = startIndex + 1; j < Math.min(lines.length, startIndex + extendedSearch); j++) {
 const nextLine = lines[j].trim();
 
 // Ignorer lignes vides mais pas trop (max 5 lignes vides consécutives)
 if (nextLine === '' && j - startIndex < 5) continue;
 
 if (nextLine.includes('SPECIFICATION JSON')) {
 // JSON peut être sur la même ligne ou après
 const jsonMatch = nextLine.match(/SPECIFICATION JSON:\s*(\{[\s\S]*)/);
 if (jsonMatch && jsonMatch[1]) {
 jsonStart = j;
 jsonStr = jsonMatch[1];
 break;
 } else {
 // Chercher JSON dans les lignes suivantes (ignorer lignes vides et "{" isolés)
 for (let k = j + 1; k < Math.min(lines.length, j + 15); k++) {
 const jsonLine = lines[k].trim();
 if (jsonLine === '') continue; // Ignorer lignes vides
 // Si c'est juste "{", chercher la suite sur les lignes suivantes
 if (jsonLine === '{') {
 // Chercher la suite du JSON
 for (let m = k + 1; m < Math.min(lines.length, k + 20); m++) {
 const nextJsonLine = lines[m].trim();
 if (nextJsonLine === '') continue;
 if (nextJsonLine.includes('"type"') || nextJsonLine.startsWith('"type"')) {
 jsonStart = k; // Commencer depuis le "{"
 jsonStr = '{' + '\n' + nextJsonLine;
 break;
 }
 }
 if (jsonStart >= 0) break;
 } else if (jsonLine.startsWith('{') && (jsonLine.includes('"type"') || jsonLine.includes('"type":'))) {
 jsonStart = k;
 jsonStr = jsonLine;
 break;
 }
 }
 if (jsonStart >= 0) break;
 }
 }
 }
 }
 
 // PATTERN 2: Ligne contient "SPECIFICATION JSON:" ou "Graphique X -"
 else if (line.includes('SPECIFICATION JSON') || /^Graphique\s+\d+\s*-/.test(line)) {
 // Chercher JSON sur la même ligne
 const jsonMatch = line.match(/(?:SPECIFICATION JSON:|Graphique\s+\d+\s*-[^}]*?)(\{[\s\S]*)/);
 if (jsonMatch && jsonMatch[1]) {
 jsonStart = startIndex;
 jsonStr = jsonMatch[1];
 } else {
 // Chercher JSON dans les lignes suivantes (ignorer lignes vides)
 for (let j = startIndex + 1; j < Math.min(lines.length, startIndex + maxSearch); j++) {
 const nextLine = lines[j].trim();
 // Ignorer lignes vides mais pas trop
 if (nextLine === '' && j - startIndex < 3) continue;
 if (nextLine.startsWith('{') && (nextLine.includes('"type"') || nextLine.includes('"type":'))) {
 jsonStart = j;
 jsonStr = nextLine;
 break;
 }
 }
 }
 }
 
 // PATTERN 2: Ligne commence par "type":
 else if (line.startsWith('"type":')) {
 jsonStart = startIndex;
 jsonStr = '{' + line;
 }
 
 // PATTERN 3: Ligne commence par { avec "type" (PRIORITÉ HAUTE)
 // Vérifier d'abord si c'est juste un "{" isolé (marqueur)
 if (line.startsWith('{') && (line.includes('"type"') || line.includes('"type":'))) {
 // Si la ligne est juste "{", chercher le JSON sur les lignes suivantes
 if (line.trim() === '{' || line.trim() === '{' + line.trim().substring(1).trim()) {
 // Chercher le JSON sur les lignes suivantes
 for (let j = startIndex + 1; j < Math.min(lines.length, startIndex + 10); j++) {
 const nextLine = lines[j].trim();
 if (nextLine && (nextLine.includes('"type"') || nextLine.startsWith('"type"'))) {
 jsonStart = startIndex; // Commencer depuis le "{"
 jsonStr = '{' + '\n' + nextLine;
 break;
 }
 }
 } else {
 jsonStart = startIndex;
 jsonStr = line;
 }
 }
 
 // PATTERN 4: Bloc ```json
 else if (line.startsWith('```json') || (line === '```' && startIndex + 1 < lines.length && lines[startIndex + 1].trim().startsWith('{'))) {
 let j = startIndex + 1;
 jsonStr = '';
 while (j < lines.length && !lines[j].trim().startsWith('```')) {
 jsonStr += lines[j] + '\n';
 j++;
 }
 jsonStart = startIndex;
 jsonEnd = j;
 }
 
 // Si on a trouvé un début de JSON, collecter le reste
 if (jsonStart >= 0 && jsonEnd < 0) {
 let braceCount = 0;
 let inString = false;
 let escapeNext = false;
 
 // Compter les accolades de la première ligne
 for (let charIdx = 0; charIdx < jsonStr.length; charIdx++) {
 const char = jsonStr[charIdx];
 if (escapeNext) {
 escapeNext = false;
 continue;
 }
 if (char === '\\') {
 escapeNext = true;
 continue;
 }
 if (char === '"' && !escapeNext) {
 inString = !inString;
 }
 if (!inString && char === '{') braceCount++;
 if (!inString && char === '}') braceCount--;
 }
 
 // Collecter les lignes suivantes jusqu'à ce que les accolades soient équilibrées
 let k = jsonStart + 1;
 while (k < lines.length && braceCount > 0 && k - jsonStart < maxSearch) {
 const nextChunk = lines[k].trim();
 if (nextChunk === '```') break;
 
 // Compter les accolades dans cette ligne
 for (let charIdx = 0; charIdx < nextChunk.length; charIdx++) {
 const char = nextChunk[charIdx];
 if (escapeNext) {
 escapeNext = false;
 continue;
 }
 if (char === '\\') {
 escapeNext = true;
 continue;
 }
 if (char === '"' && !escapeNext) {
 inString = !inString;
 }
 if (!inString && char === '{') braceCount++;
 if (!inString && char === '}') braceCount--;
 }
 
 jsonStr += '\n' + nextChunk;
 k++;
 
 if (braceCount === 0) {
 jsonEnd = k;
 break;
 }
 }
 
 if (jsonEnd < 0 && braceCount === 0) {
 jsonEnd = k;
 }
 }
 
 // Parser le JSON si on a trouvé quelque chose
 // Si jsonEnd n'est pas défini mais qu'on a jsonStr, essayer de le parser quand même
 if (jsonStr && (jsonEnd >= 0 || jsonStr.trim().length > 10)) {
 try {
 // Nettoyer le JSON
 let cleaned = jsonStr
 .replace(/,\s*}/g, '}')
 .replace(/,\s*]/g, ']')
 .replace(/```/g, '')
 .trim();
 
 // S'assurer qu'on commence et finit correctement
 if (!cleaned.startsWith('{')) {
 cleaned = '{' + cleaned;
 }
 if (!cleaned.endsWith('}')) {
 cleaned += '}';
 }

 // Réparer le JSON mal formé
 cleaned = this.repairChartJSON(cleaned);

 const chart = JSON.parse(cleaned);
 
 // NETTOYER le titre si il contient des artefacts (comme "{")
 if (chart.title && typeof chart.title === 'string') {
 chart.title = chart.title
 .replace(/^\{\s*/, '') // Retirer "{" au début
 .replace(/\s*\}\s*$/, '') // Retirer "}" à la fin
 .trim();
 }
 
 // Valider que c'est bien un graphique
 if (chart && (chart.type || chart.chartType) && chart.data && Array.isArray(chart.data) && chart.data.length > 0) {
 // Normaliser le type AVANT de logger
 chart.type = this.normalizeChartType(chart.type || chart.chartType);
 
 // DEBUG: Logger pour diagnostiquer les graphiques détectés
 logger.log('[PDF] ✅ Graphique validé:', {
 type: chart.type,
 originalType: chart.type || chart.chartType,
 title: chart.title || 'Sans titre',
 dataLength: chart.data.length,
 hasTitle: !!chart.title
 });
 
 logger.log(`[PDF] Chart détecté et parsé:`, {
 type: chart.type,
 title: chart.title || 'Sans titre',
 dataLength: chart.data.length,
 startLine: jsonStart,
 endLine: jsonEnd
 });
 
 return { chart, endIndex: jsonEnd };
 }
 } catch (e) {
 logger.log('[PDF] Échec parsing JSON graphique:', e);
 }
 }
 
 // Si on a jsonStr mais pas jsonEnd, essayer quand même de parser (JSON sur une seule ligne ou mal détecté)
 if (jsonStr && jsonStr.trim().length > 10 && jsonStart >= 0) {
 try {
 let cleaned = jsonStr.trim();
 if (!cleaned.startsWith('{')) cleaned = '{' + cleaned;
 if (!cleaned.endsWith('}')) cleaned += '}';
 cleaned = this.repairChartJSON(cleaned);
 const chart = JSON.parse(cleaned);
 
 if (chart && (chart.type || chart.chartType) && chart.data && Array.isArray(chart.data) && chart.data.length > 0) {
 chart.type = this.normalizeChartType(chart.type || chart.chartType);
 
 // Nettoyer le titre
 if (chart.title && typeof chart.title === 'string') {
 chart.title = chart.title.replace(/^\{\s*/, '').replace(/\s*\}\s*$/, '').trim();
 }
 
 logger.log('[PDF] ✅ Graphique détecté (sans jsonEnd):', {
 type: chart.type,
 title: chart.title || 'Sans titre',
 dataLength: chart.data.length
 });
 
 // Estimer jsonEnd basé sur la longueur du JSON
 const estimatedEnd = jsonStart + Math.ceil(jsonStr.length / 80); // ~80 chars par ligne
 return { chart, endIndex: Math.min(estimatedEnd, lines.length - 1) };
 }
 } catch (e) {
 logger.log('[PDF] Échec parsing JSON (fallback):', e);
 }
 }
 
 return { chart: null, endIndex: startIndex };
 }
 
 /**
 * Parse le contenu et ajoute tout - REFACTORISÉ POUR ROBUSTESSE
 */
 parseAndAddContent(content: string): void {
 const lines = content.split('\n');
 let i = 0;
 
 logger.log('PDF Parsing - Total lines:', lines.length);
 
 while (i < lines.length) {
 const line = lines[i].trim();
 
 if (!line) {
 this.y += CONFIG.paragraphSpacing;
 i++;
 continue;
 }
 
 // PRIORITÉ 1: Détecter les graphiques AVANT tout autre traitement
 // Cela évite d'afficher les "{" ou autres marqueurs en brut
 const chartResult = this.tryParseChart(lines, i);
 if (chartResult.chart) {
 const chart = chartResult.chart;
 
 // Rendre le graphique selon son type (normalisé)
 const normalizedType = this.normalizeChartType(chart.type);
 if (normalizedType === 'bar') this.addBarChart(chart);
 else if (normalizedType === 'pie') this.addPieChart(chart);
 else if (normalizedType === 'line') this.addLineChart(chart);
 else if (normalizedType === 'scatter') this.addScatterChart(chart);

 // Avancer jusqu'à la fin du JSON (ou au moins jusqu'à la ligne suivante si endIndex invalide)
 i = chartResult.endIndex > i ? chartResult.endIndex : i + 1;
 continue;
 }
 
 // ÉVITER d'afficher les lignes qui commencent par "{" si elles font partie d'un graphique
 // Si la ligne commence par "{" et contient "type", c'est probablement un graphique
 if (line.startsWith('{') && (line.includes('"type"') || line.includes('"type":'))) {
 // Essayer de parser (déjà fait plus haut, mais double vérification)
 const lastChance = this.tryParseChart(lines, i);
 if (lastChance.chart) {
 const chart = lastChance.chart;
 if (chart.type === 'bar') this.addBarChart(chart);
 else if (chart.type === 'pie') this.addPieChart(chart);
 else if (chart.type === 'line') this.addLineChart(chart);
 else if (chart.type === 'scatter') this.addScatterChart(chart);
 i = lastChance.endIndex > i ? lastChance.endIndex : i + 1;
 continue;
 }
 // Si on ne peut pas parser, ignorer la ligne (c'est du JSON brut qu'on ne peut pas afficher)
 // Vérifier si c'est juste un "{" isolé ou un marqueur
 if (line.trim() === '{' || (line.trim().startsWith('{') && line.trim().length < 10 && !line.includes('"type"'))) {
 // C'est probablement juste un marqueur, ignorer silencieusement
 i++;
 continue;
 }
 // Ligne JSON non parsable - ignorer pour éviter d'afficher du JSON brut
 logger.warn('[PDF] Ligne JSON non parsable ignorée:', line.substring(0, 100));
 i++;
 continue;
 }
 
 // ÉVITER d'afficher "SPECIFICATION JSON:" seul (sans JSON suivant)
 if (line.includes('SPECIFICATION JSON') && !line.includes('{')) {
 // Vérifier si le JSON est sur les lignes suivantes
 let foundJson = false;
 let jsonLineIndex = -1;
 for (let j = i + 1; j < Math.min(lines.length, i + 5); j++) {
 const nextLine = lines[j].trim();
 // Ignorer les lignes vides
 if (nextLine === '') continue;
 if (nextLine.startsWith('{')) {
 foundJson = true;
 jsonLineIndex = j;
 break;
 }
 }
 if (!foundJson) {
 // Pas de JSON suivant, ignorer cette ligne
 i++;
 continue;
 }
 // Si on a trouvé le JSON, sauter directement à cette ligne pour le parser
 // Cela évite d'afficher "SPECIFICATION JSON:" seul
 if (jsonLineIndex > i) {
 i = jsonLineIndex - 1; // On reviendra à jsonLineIndex au prochain tour
 continue;
 }
 }
 
 // ÉVITER d'afficher les lignes qui sont juste "{" (marqueur de début JSON)
 // Surtout si elles suivent "SPECIFICATION JSON:" ou un titre de graphique
 if (line.trim() === '{' || (line.trim() === '{' && line.length === 1)) {
 // Vérifier si c'est suivi d'un JSON valide dans les lignes suivantes
 let isJsonStart = false;
 for (let j = i + 1; j < Math.min(lines.length, i + 10); j++) {
 const nextLine = lines[j].trim();
 if (nextLine && (nextLine.includes('"type"') || nextLine.startsWith('"type"'))) {
 isJsonStart = true;
 break;
 }
 }
 if (isJsonStart) {
 // C'est le début d'un JSON, ne pas l'afficher, il sera parsé par tryParseChart
 // Mais d'abord, vérifier si la ligne précédente était "SPECIFICATION JSON:"
 const prevLine = i > 0 ? lines[i - 1].trim() : '';
 if (prevLine.includes('SPECIFICATION JSON') || prevLine.includes('Graphique')) {
 // Ignorer ce "{" car il sera inclus dans le parsing du graphique
 i++;
 continue;
 }
 }
 }
 
 // Détecter les titres markdown (sauf si c'est un titre de graphique déjà traité)
 if (line.startsWith('###') && !/Graphique\s+\d+\s*-/.test(line)) {
 this.addHeading(line.replace(/^###\s*/, ''), 3);
 } else if (line.startsWith('##')) {
 this.addHeading(line.replace(/^##\s*/, ''), 2);
 } else if (line.startsWith('#')) {
 this.addHeading(line.replace(/^#\s*/, ''), 1);
 }
 // Détecter les tableaux markdown
 else if (line.includes('|') && line.split('|').length > 2) {
 const tableRows: string[][] = [];
 let j = i;
 
 while (j < lines.length && lines[j].includes('|')) {
 const row = lines[j]
 .split('|')
 .map(cell => cell.trim())
 .filter(cell => cell && !cell.match(/^-+$/));
 
 if (row.length > 0) {
 tableRows.push(row);
 }
 j++;
 }
 
 if (tableRows.length > 0) {
 this.addTable(tableRows);
 i = j - 1;
 }
 }
 // Texte normal
 else {
 this.addParagraph(line);
 }
 
 i++;
 }
 }
 
 /**
 * Génère et retourne le PDF
 */
 generate(): Blob {
 return this.pdf.output('blob');
 }
 
 /**
 * Télécharge le PDF
 */
 download(filename: string = 'export.pdf'): void {
 this.pdf.save(filename);
 }
}

// ==========================================
// FONCTIONS D'EXPORT RAPIDE
// ==========================================

export async function exportToPDF(content: string, options: PDFOptions): Promise<Blob> {
 const exporter = new PDFExporter(options);
 exporter.parseAndAddContent(content);
 return exporter.generate();
}

export async function downloadPDF(content: string, options: PDFOptions, filename: string = 'export.pdf'): Promise<void> {
 const exporter = new PDFExporter(options);
 exporter.parseAndAddContent(content);
 exporter.download(filename);
}
